import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Pencil, Trash2, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSession } from '@/hooks/useSession';
import { useAddQuoteNote, useEditQuoteNote, useDeleteQuoteNote } from '../../hooks';
import type { V3QuoteNote } from '../../types';

interface QuoteNotesProps {
  quoteId: string;
  notes: V3QuoteNote[];
}

// Real quote notes can arrive without a `date` (despite the type) — legacy docs
// and some write paths omit it. parseISO(undefined)/format(Invalid Date) throws
// and took down the entire quote edit modal (#100102 sighting), so parse safely.
function formatNoteDate(date?: string): string {
  if (!date) return '';
  const parsed = parseISO(date);
  return Number.isNaN(parsed.getTime()) ? '' : format(parsed, 'MMM d, yyyy HH:mm');
}

function noteTime(date?: string): number {
  if (!date) return 0;
  const t = new Date(date).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function QuoteNotes({ quoteId, notes }: QuoteNotesProps) {
  const { t } = useTranslation('quotes');
  const { user } = useSession();
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);

  const addMutation = useAddQuoteNote();
  const editMutation = useEditQuoteNote();
  const deleteMutation = useDeleteQuoteNote();

  const visibleNotes = notes
    .filter((n) => !n.deleted)
    .sort((a, b) => noteTime(b.date) - noteTime(a.date));

  const currentUserId = user?._id;

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    addMutation.mutate(
      { quoteId, data: { content: newNote.trim() } },
      { onSuccess: () => setNewNote('') }
    );
  };

  const handleStartEdit = (note: V3QuoteNote) => {
    setEditingNoteId(note._id);
    setEditContent(note.editedContent || note.content);
  };

  const handleSaveEdit = () => {
    if (!editingNoteId || !editContent.trim()) return;
    editMutation.mutate(
      { quoteId, noteId: editingNoteId, content: editContent.trim() },
      { onSuccess: () => setEditingNoteId(null) }
    );
  };

  const handleDeleteConfirm = () => {
    if (!deletingNoteId) return;
    deleteMutation.mutate(
      { quoteId, noteId: deletingNoteId },
      { onSuccess: () => setDeletingNoteId(null) }
    );
  };

  return (
    <div className="space-y-4">
      {/* Add note */}
      <div className="space-y-2">
        <Textarea
          placeholder={t('notes.addPlaceholder')}
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          rows={2}
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleAddNote}
            disabled={!newNote.trim() || addMutation.isPending}
          >
            <Send className="h-4 w-4 mr-1" />
            {addMutation.isPending ? t('notes.adding') : t('notes.addButton')}
          </Button>
        </div>
      </div>

      {/* Notes list */}
      {visibleNotes.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">{t('notes.noNotes')}</p>
      ) : (
        <div className="space-y-3">
          {visibleNotes.map((note) => {
            const isOwn = note.user === currentUserId;
            const isEditing = editingNoteId === note._id;
            const displayContent = note.editedContent || note.content;

            return (
              <div key={note._id} className="border rounded-lg p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{note.user_name}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatNoteDate(note.date)}
                    </span>
                    {note.editedContent && (
                      <span className="text-xs text-muted-foreground italic">{t('notes.edited')}</span>
                    )}
                  </div>
                  {isOwn && !isEditing && (
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleStartEdit(note)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-muted-foreground hover:text-destructive"
                        onClick={() => setDeletingNoteId(note._id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={2}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingNoteId(null)}
                      >
                        {t('notes.cancel')}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={editMutation.isPending}
                      >
                        {t('notes.save')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{displayContent}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deletingNoteId} onOpenChange={(open) => !open && setDeletingNoteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('notes.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('notes.deleteMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('notes.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t('notes.deleting', 'Deleting...') : t('delete.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
